import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {AudienceModel, CreateOrUpdateAudienceModel} from '../../../redux/inAppMessageTypes'
import EmployerSelector from '../../EmployerSelector'
import {SearchResultItem} from '../../SearchAndSelectField'
import {Colors} from '../../../constants/colors'
import S2 from '../../typography/S2'

interface Props {
  audience: Extract<AudienceModel, { type: 'employer' }> | undefined
  onChange: (audience: Extract<CreateOrUpdateAudienceModel, { type: 'employer' }>) => void
}

const EmployerAudienceEditor = ({audience, onChange}: Props) => {
  const styles = useStyles()
  const [selectedEmployers, setSelectedEmployers] = useState<SearchResultItem[]>([])

  useEffect(() => {
    if (!!audience) {
      const mappedEmployers = audience.employers.map(employer => ({
        name: employer.name,
        value: employer.id
      }))
      setSelectedEmployers(mappedEmployers)
    } else {
      setSelectedEmployers([])
    }
  }, [audience])

  useEffect(() => {
    const next: CreateOrUpdateAudienceModel = {
      type: 'employer',
      employerIds: selectedEmployers.map(x => x.value)
    }
    onChange(next)
  }, [selectedEmployers, onChange]);

  const handleEmployerSelected = (item: SearchResultItem | null) => {
    if (!item) return
    if (selectedEmployers.some(x => x.value === item.value)) return
    setSelectedEmployers(prev => [...prev, item])
  }

  const handleRemove = (value: string) => {
    setSelectedEmployers(prev => prev.filter(e => e.value !== value))
  }

  const renderSelected = () => {
    if (selectedEmployers.length === 0) {
      return <S2 className={styles.hint}>No employers selected. Search to add employers.</S2>
    }

    return (
      <div className={styles.chipsContainer}>
        {selectedEmployers.map(e => (
          <div className={styles.chip} key={e.value}>
            <span className={styles.chipText}>{e.name}</span>
            <button type='button' className={styles.removeBtn} onClick={() => handleRemove(e.value)}>×</button>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <EmployerSelector onEmployerSelected={handleEmployerSelected} value='' clearOnSelect/>
      {renderSelected()}
    </div>
  )
}

export default EmployerAudienceEditor

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 8
  },
  chipsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 12,
    background: Colors.darkGray3,
  },
  chipText: {
    fontFamily: 'Aestetico-Regular',
    color: Colors.gray0,
  },
  removeBtn: {
    border: 'none',
    background: 'transparent',
    color: Colors.gray0,
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
    width: 20,
    height: 20,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    '&:hover': {
      background: Colors.darkGray4
    }
  },
  hint: {
    color: Colors.gray0,
  }
})
